/**
 * Category-specific pricing defaults for vendor packages.
 * Used in PricingSetupScreen to pre-populate package templates
 * with relevant pricing types, features, and add-ons per vendor category.
 */

import type { VendorCategory } from "../types";

export type PricingType = "flat" | "per_person" | "hourly" | "custom";

export interface CategoryPricingDefault {
  defaultPricingType: PricingType;
  pricingUnit?: string; // e.g. "per person", "per hour"
  showQuantityRange?: boolean; // Show min/max inputs for per_person/hourly
  suggestedFeatures: string[][]; // Features per package tier [basic, standard, premium]
  suggestedAddOns: { name: string; price: number; pricingType?: PricingType }[];
  packageNameSuggestions: string[]; // e.g. ["Essential", "Classic", "Premium"]
  featurePlaceholders: string[]; // Placeholder hints for empty feature fields
}

export const CATEGORY_PRICING_DEFAULTS: Record<
  string,
  CategoryPricingDefault
> = {
  Venue: {
    defaultPricingType: "per_person",
    pricingUnit: "per person",
    showQuantityRange: true,
    packageNameSuggestions: ["Intimate", "Classic", "Grand"],
    featurePlaceholders: [
      "X hour venue rental",
      "Tables & chairs for X guests",
      "Setup & cleanup included",
      "On-site coordinator",
      "Parking for X cars",
      "Indoor/outdoor space access",
      "Sound system & microphone",
      "Bridal suite access",
    ],
    suggestedFeatures: [
      // Basic
      [
        "4 hour venue rental",
        "Tables & chairs",
        "Basic setup & cleanup",
        "Parking",
      ],
      // Standard
      [
        "6 hour venue rental",
        "Tables & chairs",
        "Full setup & cleanup",
        "On-site coordinator",
        "Parking",
        "Sound system",
      ],
      // Premium
      [
        "8 hour venue rental",
        "Premium tables & chairs",
        "Full setup & cleanup",
        "Dedicated coordinator",
        "Valet parking",
        "Sound system & lighting",
        "Bridal suite",
        "Day-of coordination",
      ],
    ],
    suggestedAddOns: [
      { name: "Extra hour", price: 500 },
      { name: "Valet parking", price: 30, pricingType: "per_person" },
      { name: "Cocktail reception setup", price: 25, pricingType: "per_person" },
      { name: "Late night fee (after midnight)", price: 1000 },
    ],
  },

  Photographer: {
    defaultPricingType: "flat",
    showQuantityRange: false,
    packageNameSuggestions: ["Essential", "Full Story", "Premium"],
    featurePlaceholders: [
      "X hours of coverage",
      "X edited photos",
      "Online gallery",
      "Print rights included",
      "Second photographer",
      "Engagement session",
      "Wedding album",
      "Same-day preview photos",
    ],
    suggestedFeatures: [
      // Basic
      [
        "4 hours of coverage",
        "200 edited photos",
        "Online gallery",
        "Print rights",
      ],
      // Standard
      [
        "8 hours of coverage",
        "500 edited photos",
        "Online gallery",
        "Print rights",
        "Second photographer",
        "Engagement session",
      ],
      // Premium
      [
        "Full day coverage (12 hours)",
        "800+ edited photos",
        "Online gallery",
        "Print rights",
        "Second photographer",
        "Engagement session",
        "Premium wedding album",
        "Same-day preview photos",
      ],
    ],
    suggestedAddOns: [
      { name: "Extra hour of coverage", price: 500 },
      { name: "Engagement session", price: 1500 },
      { name: "Premium album design", price: 2000 },
      { name: "Canvas print (24x36)", price: 800 },
    ],
  },

  Videographer: {
    defaultPricingType: "flat",
    showQuantityRange: false,
    packageNameSuggestions: ["Highlights", "Full Story", "Cinematic"],
    featurePlaceholders: [
      "X hours of filming",
      "Highlight reel (X minutes)",
      "Full ceremony edit",
      "Drone footage",
      "Same-day edit",
      "Raw footage included",
      "Second videographer",
      "Social media teaser",
    ],
    suggestedFeatures: [
      // Basic
      [
        "6 hours of filming",
        "3-5 minute highlight reel",
        "Ceremony edit",
        "Online delivery",
      ],
      // Standard
      [
        "10 hours of filming",
        "5-7 minute highlight reel",
        "Full ceremony & speeches edit",
        "Drone footage",
        "Online delivery",
      ],
      // Premium
      [
        "Full day filming (14 hours)",
        "8-10 minute cinematic film",
        "Full ceremony & speeches edit",
        "Drone footage",
        "Same-day edit",
        "Second videographer",
        "Raw footage",
        "Social media teaser",
      ],
    ],
    suggestedAddOns: [
      { name: "Extra hour of filming", price: 500 },
      { name: "Same-day edit", price: 2000 },
      { name: "Raw footage", price: 800 },
      { name: "Drone footage add-on", price: 1000 },
    ],
  },

  DJ: {
    defaultPricingType: "hourly",
    pricingUnit: "per hour",
    showQuantityRange: true,
    packageNameSuggestions: ["Basic Set", "Full Event", "Premium Experience"],
    featurePlaceholders: [
      "Professional sound system",
      "Dance floor lighting",
      "MC services",
      "Music consultation",
      "Backup equipment",
      "Wireless microphone",
      "Custom playlist curation",
      "Fog machine",
    ],
    suggestedFeatures: [
      // Basic
      [
        "Professional sound system",
        "Basic lighting",
        "Music consultation",
        "Backup equipment",
      ],
      // Standard
      [
        "Premium sound system",
        "Dance floor lighting",
        "MC services",
        "Music consultation",
        "Wireless microphone",
        "Backup equipment",
      ],
      // Premium
      [
        "Premium sound system",
        "Full lighting design",
        "MC services",
        "Music consultation",
        "Wireless microphone",
        "Fog machine",
        "Custom playlist curation",
        "Backup equipment",
        "Early setup",
      ],
    ],
    suggestedAddOns: [
      { name: "Extra hour", price: 300 },
      { name: "Fog machine", price: 200 },
      { name: "Photo booth", price: 1500 },
      { name: "Uplighting package", price: 800 },
    ],
  },

  Band: {
    defaultPricingType: "flat",
    showQuantityRange: false,
    packageNameSuggestions: ["Acoustic Set", "Full Band", "Premium Show"],
    featurePlaceholders: [
      "X hours of live music",
      "PA system included",
      "Song request list",
      "Cocktail hour acoustic set",
      "Custom song learning",
      "MC services",
      "Sound check included",
      "Travel within X km",
    ],
    suggestedFeatures: [
      // Basic
      [
        "2 hours of live music",
        "3-piece band",
        "PA system",
        "Standard song list",
      ],
      // Standard
      [
        "3 hours of live music",
        "5-piece band",
        "PA system",
        "Song requests",
        "Cocktail hour acoustic set",
        "Sound check",
      ],
      // Premium
      [
        "4 hours of live music",
        "7-piece band",
        "Premium PA system",
        "Custom song learning (up to 3)",
        "Cocktail hour acoustic set",
        "MC services",
        "Sound check",
        "Encore set",
      ],
    ],
    suggestedAddOns: [
      { name: "Extra set (45 min)", price: 1000 },
      { name: "Learn a special song", price: 500 },
      { name: "Additional musician", price: 800 },
    ],
  },

  Caterer: {
    defaultPricingType: "per_person",
    pricingUnit: "per person",
    showQuantityRange: true,
    packageNameSuggestions: ["Essential", "Gourmet", "Premium"],
    featurePlaceholders: [
      "X course menu",
      "Service staff included",
      "Table settings & linens",
      "Dietary accommodations",
      "Tasting session",
      "Custom menu design",
      "Bar service",
      "Late-night snacks",
    ],
    suggestedFeatures: [
      // Basic
      [
        "3 course menu",
        "Service staff",
        "Basic table settings",
        "Dietary accommodations",
      ],
      // Standard
      [
        "4 course menu",
        "Service staff",
        "Premium table settings & linens",
        "Dietary accommodations",
        "Tasting session for 2",
        "Custom menu options",
      ],
      // Premium
      [
        "5 course gourmet menu",
        "Premium service staff",
        "Luxury table settings & linens",
        "Full dietary accommodations",
        "Tasting session for 4",
        "Custom menu design",
        "Champagne toast",
        "Late-night snack station",
      ],
    ],
    suggestedAddOns: [
      { name: "Cocktail hour appetizers", price: 25, pricingType: "per_person" },
      { name: "Kids menu", price: 40, pricingType: "per_person" },
      { name: "Open bar service", price: 50, pricingType: "per_person" },
      { name: "Dessert station", price: 20, pricingType: "per_person" },
      { name: "Late-night snacks", price: 15, pricingType: "per_person" },
    ],
  },

  Florist: {
    defaultPricingType: "flat",
    showQuantityRange: false,
    packageNameSuggestions: ["Garden", "Romantic", "Luxe"],
    featurePlaceholders: [
      "Bridal bouquet",
      "X bridesmaids bouquets",
      "X centerpieces",
      "Ceremony arch flowers",
      "Boutonnières",
      "Corsages",
      "Delivery & setup",
      "Petal toss bags",
    ],
    suggestedFeatures: [
      // Basic
      [
        "Bridal bouquet",
        "2 bridesmaids bouquets",
        "5 centerpieces",
        "Delivery & setup",
      ],
      // Standard
      [
        "Premium bridal bouquet",
        "4 bridesmaids bouquets",
        "10 centerpieces",
        "Ceremony arch flowers",
        "Boutonnières (5)",
        "Delivery & setup",
      ],
      // Premium
      [
        "Luxury bridal bouquet",
        "6 bridesmaids bouquets",
        "15 centerpieces",
        "Ceremony arch flowers",
        "Boutonnières (8)",
        "Corsages (4)",
        "Aisle petal decor",
        "Delivery, setup & breakdown",
        "Consultation included",
      ],
    ],
    suggestedAddOns: [
      { name: "Additional centerpiece", price: 150 },
      { name: "Aisle petal decor", price: 400 },
      { name: "Corsage", price: 50 },
      { name: "Flower crown", price: 200 },
    ],
  },

  Decor: {
    defaultPricingType: "flat",
    showQuantityRange: false,
    packageNameSuggestions: ["Classic", "Styled", "Luxury"],
    featurePlaceholders: [
      "Venue styling consultation",
      "Table decor & settings",
      "Ceremony setup",
      "Lighting design",
      "Backdrop installation",
      "Lounge furniture",
      "Signage & welcome board",
      "Setup & breakdown",
    ],
    suggestedFeatures: [
      // Basic
      [
        "Venue styling consultation",
        "Basic table decor",
        "Ceremony setup",
        "Setup & breakdown",
      ],
      // Standard
      [
        "Full venue styling",
        "Table decor & centerpieces",
        "Ceremony setup",
        "Uplighting",
        "Welcome signage",
        "Setup & breakdown",
      ],
      // Premium
      [
        "Complete venue transformation",
        "Luxury table decor",
        "Ceremony design",
        "Full lighting design",
        "Backdrop installation",
        "Lounge furniture",
        "Welcome signage & seating chart",
        "Setup & full breakdown",
        "Consultation included",
      ],
    ],
    suggestedAddOns: [
      { name: "Extra table setup", price: 100 },
      { name: "Balloon arch", price: 500 },
      { name: "Neon sign rental", price: 300 },
      { name: "Photo backdrop", price: 400 },
    ],
  },

  Baker: {
    defaultPricingType: "flat",
    showQuantityRange: false,
    packageNameSuggestions: ["Classic", "Designer", "Showstopper"],
    featurePlaceholders: [
      "X-tier cake",
      "Custom design consultation",
      "Tasting session included",
      "Delivery & setup",
      "Fondant/buttercream finish",
      "Sugar flower topper",
      "Dessert table items",
      "Cake cutting set rental",
    ],
    suggestedFeatures: [
      // Basic
      [
        "2-tier cake (50 servings)",
        "Buttercream finish",
        "Basic design",
        "Delivery & setup",
      ],
      // Standard
      [
        "3-tier cake (100 servings)",
        "Custom design consultation",
        "Fondant or buttercream finish",
        "Tasting session",
        "Delivery & setup",
      ],
      // Premium
      [
        "4-tier cake (150+ servings)",
        "Premium custom design",
        "Fondant finish with details",
        "Sugar flower topper",
        "Tasting session",
        "Delivery & setup",
        "Dessert table (3 items)",
        "Cake cutting set rental",
      ],
    ],
    suggestedAddOns: [
      { name: "Extra tier", price: 300 },
      { name: "Sugar flowers", price: 200 },
      { name: "Dessert table (per item)", price: 150 },
      { name: "Cupcake tower (50 pcs)", price: 400 },
    ],
  },

  // ── Multi-vertical additions (2026-07) ──
  // The map is string-keyed, so these rows are additive-only.
  //
  // `defaultPricingType: "flat"` is the PERSISTED value for packages mode
  // (legacy naming — see PricingMode in config/vendorCapabilities.ts, where
  // the UI presents it as "Packages"/חבילות). The itemized verticals
  // (DesignFlowers, BridalDress) also persist "flat" here; their composer
  // branch is chosen from the capability map's pricingMode, not from this
  // field, so the two are not in conflict.

  DesignFlowers: {
    defaultPricingType: "flat",
    showQuantityRange: false,
    packageNameSuggestions: ["Garden", "Styled", "Luxe"],
    featurePlaceholders: [
      "Bridal bouquet",
      "X centerpieces",
      "Ceremony arch / chuppah flowers",
      "Stage & backdrop design",
      "Lighting design",
      "Draping",
      "Delivery & setup",
      "Breakdown & strike",
    ],
    suggestedFeatures: [
      // Basic
      [
        "Bridal bouquet",
        "5 centerpieces",
        "Basic table styling",
        "Delivery & setup",
      ],
      // Standard
      [
        "Premium bridal bouquet",
        "10 centerpieces",
        "Ceremony arch flowers",
        "Table styling & linens",
        "Delivery & setup",
      ],
      // Premium
      [
        "Luxury bridal bouquet",
        "15+ centerpieces",
        "Ceremony arch / chuppah design",
        "Stage & backdrop design",
        "Lighting design",
        "Full table styling",
        "Delivery, setup & breakdown",
        "Design consultation included",
      ],
    ],
    suggestedAddOns: [
      { name: "Additional centerpiece", price: 150 },
      { name: "Aisle petal decor", price: 400 },
      { name: "Backdrop rental", price: 800 },
      { name: "Lighting package", price: 1200 },
    ],
  },

  Planner: {
    defaultPricingType: "flat",
    showQuantityRange: false,
    packageNameSuggestions: ["Day-Of", "Partial", "Full Production"],
    featurePlaceholders: [
      "Planning meetings (X sessions)",
      "Vendor sourcing & management",
      "Budget tracking",
      "Day-of timeline",
      "On-site coordination (X hours)",
      "Design & styling direction",
      "RSVP management",
      "Post-event wrap-up",
    ],
    suggestedFeatures: [
      // Basic
      [
        "2 planning meetings",
        "Day-of timeline",
        "On-site coordination (8 hours)",
        "Vendor confirmations",
      ],
      // Standard
      [
        "Monthly planning meetings",
        "Vendor sourcing & management",
        "Budget tracking",
        "Day-of timeline",
        "On-site coordination (10 hours)",
      ],
      // Premium
      [
        "Unlimited planning meetings",
        "Full vendor sourcing & management",
        "Budget planning & tracking",
        "Design & styling direction",
        "Day-of timeline & rehearsal",
        "On-site team (2 coordinators)",
        "Post-event wrap-up",
      ],
    ],
    suggestedAddOns: [
      { name: "Additional coordinator", price: 1000 },
      { name: "Extra on-site hour", price: 250, pricingType: "hourly" },
      { name: "Rehearsal coordination", price: 800 },
    ],
  },

  MakeupHair: {
    defaultPricingType: "flat",
    showQuantityRange: false,
    packageNameSuggestions: ["Bride", "Bride & Party", "Full Glam"],
    featurePlaceholders: [
      "Bridal makeup",
      "Bridal hair styling",
      "Trial session",
      "Party makeup (per person)",
      "Party hair (per person)",
      "Lashes included",
      "Travel to venue",
      "Stay-through touch-ups",
    ],
    suggestedFeatures: [
      // Basic
      [
        "Bridal makeup",
        "Lashes included",
        "Touch-up kit",
      ],
      // Standard
      [
        "Bridal makeup & hair",
        "Trial session",
        "Lashes included",
        "Travel to venue",
      ],
      // Premium
      [
        "Bridal makeup & hair",
        "Trial session included",
        "Premium lashes",
        "Travel to venue",
        "Stay-through touch-ups",
        "2 party members included",
      ],
    ],
    // B3.6 (Yuval): the trial (ניסיון) is NOT billed by default — the ₪400
    // "Trial session" auto-item was removed. The trial pipeline column + the
    // trial meeting type stay as-is (they live in the capability/appointment
    // config, not here).
    suggestedAddOns: [
      { name: "Party makeup (per person)", price: 350 },
      { name: "Party hair (per person)", price: 300 },
      { name: "Stay-through touch-ups", price: 800 },
    ],
  },

  // BridalDress (שמלות כלה). ITEMIZED, not packages: a bride buys ONE gown
  // plus alterations and accessories, and she chooses it by looking — the
  // image-led proposal is the right artifact, so there are no package tiers
  // to suggest. `suggestedFeatures` stays a single tier because the composer
  // reads index 0 for itemized vendors.
  //
  // Prices are the salon-side defaults a vendor overwrites immediately; they
  // exist so the pricing tool is never an empty cliff (B3.5), not because we
  // know any given salon's rates.
  BridalDress: {
    defaultPricingType: "flat",
    showQuantityRange: false,
    packageNameSuggestions: ["Rental", "Sale", "Made to measure"],
    featurePlaceholders: [
      "Gown rental",
      "Alterations included",
      "Veil",
      "Accessories",
      "Cleaning",
      "Security deposit (refundable)",
    ],
    suggestedFeatures: [
      [
        "Gown rental",
        "2 alteration fittings included",
        "Steaming before collection",
      ],
    ],
    // A security deposit is deliberately ABSENT here. It is a held liability,
    // not revenue (see Lead.securityDepositAmount) — putting it in the
    // add-on list would push it onto quotes and into revenue totals.
    suggestedAddOns: [
      { name: "Extra alteration fitting", price: 250 },
      { name: "Veil", price: 400 },
      { name: "Cleaning", price: 250 },
      { name: "Late return (per day)", price: 200 },
    ],
  },
};

/**
 * Get pricing defaults for a vendor category.
 * Returns undefined if category not found (graceful fallback).
 */
export function getCategoryPricingDefaults(
  category: VendorCategory | string | null
): CategoryPricingDefault | undefined {
  if (!category) return undefined;
  return CATEGORY_PRICING_DEFAULTS[category];
}

/**
 * Get feature placeholders for a category.
 * Returns generic placeholders if category not found.
 */
export function getFeaturePlaceholders(
  category: VendorCategory | string | null
): string[] {
  const defaults = getCategoryPricingDefaults(category);
  return (
    defaults?.featurePlaceholders || [
      "Describe what's included",
      "Add another feature",
      "What makes this package special",
    ]
  );
}

/**
 * Get suggested package names for a category.
 */
export function getPackageNameSuggestions(
  category: VendorCategory | string | null
): string[] {
  const defaults = getCategoryPricingDefaults(category);
  return defaults?.packageNameSuggestions || ["Basic", "Standard", "Premium"];
}

/**
 * Auto-generate an availability label from months and days.
 * e.g. months [6,7,8] + days [4] → "Summer Thursdays"
 */
export function generateAvailabilityLabel(
  months?: number[],
  daysOfWeek?: number[]
): string {
  const parts: string[] = [];

  if (months && months.length > 0 && months.length < 12) {
    // Map months to season-like labels
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];

    // Check for common season patterns
    const sortedMonths = [...months].sort((a, b) => a - b);
    const isSummer =
      JSON.stringify(sortedMonths) === JSON.stringify([6, 7, 8]) ||
      JSON.stringify(sortedMonths) === JSON.stringify([5, 6, 7, 8]) ||
      JSON.stringify(sortedMonths) === JSON.stringify([5, 6, 7, 8, 9]);
    const isWinter =
      JSON.stringify(sortedMonths) === JSON.stringify([1, 2, 12]) ||
      JSON.stringify(sortedMonths) === JSON.stringify([11, 12, 1, 2].sort((a, b) => a - b));
    const isSpring =
      JSON.stringify(sortedMonths) === JSON.stringify([3, 4, 5]);
    const isFall =
      JSON.stringify(sortedMonths) === JSON.stringify([9, 10, 11]);

    if (isSummer) parts.push("Summer");
    else if (isWinter) parts.push("Winter");
    else if (isSpring) parts.push("Spring");
    else if (isFall) parts.push("Fall");
    else if (months.length <= 3) {
      parts.push(months.map((m) => monthNames[m - 1]).join(", "));
    } else {
      parts.push(
        `${monthNames[sortedMonths[0] - 1]}-${monthNames[sortedMonths[sortedMonths.length - 1] - 1]}`
      );
    }
  }

  if (daysOfWeek && daysOfWeek.length > 0 && daysOfWeek.length < 7) {
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const sortedDays = [...daysOfWeek].sort((a, b) => a - b);

    // Check for common patterns
    const isWeekdays =
      JSON.stringify(sortedDays) === JSON.stringify([1, 2, 3, 4, 5]);
    const isWeekends =
      JSON.stringify(sortedDays) === JSON.stringify([0, 6]) ||
      JSON.stringify(sortedDays) === JSON.stringify([5, 6]) ||
      JSON.stringify(sortedDays) === JSON.stringify([0, 5, 6]);

    if (isWeekdays) parts.push("Weekdays");
    else if (isWeekends) parts.push("Weekends");
    else if (daysOfWeek.length <= 3) {
      parts.push(sortedDays.map((d) => dayNames[d]).join(", "));
    } else {
      parts.push(
        `${dayNames[sortedDays[0]]}-${dayNames[sortedDays[sortedDays.length - 1]]}`
      );
    }
  }

  return parts.length > 0 ? parts.join(" ") : "Limited Availability";
}
